import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useWorkoutStore } from '@/stores/workoutStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import {
  Plus,
  Search,
  Filter,
  Play,
  Clock,
  Target,
  Dumbbell,
  Calendar,
  Edit,
  Trash2,
  Star,
  TrendingUp
} from 'lucide-react'
import { formatDuration, formatDate } from '@/lib/utils'

const WorkoutPlanner = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [showCreateForm, setShowCreateForm] = useState(false)
  
  const { workouts, exercises, addWorkout, startWorkout, deleteWorkout } = useWorkoutStore()
  const { toast } = useToast()

  const categories = [
    { id: 'all', name: 'All Workouts', icon: Dumbbell },
    { id: 'strength', name: 'Strength', icon: Dumbbell },
    { id: 'cardio', name: 'Cardio', icon: TrendingUp },
    { id: 'flexibility', name: 'Flexibility', icon: Target },
    { id: 'custom', name: 'Custom', icon: Star }
  ]

  const filteredWorkouts = workouts.filter(workout => {
    const matchesSearch = workout.name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || workout.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleStartWorkout = (workout) => {
    startWorkout(workout)
    toast({
      title: "Workout Started!",
      description: `${workout.name} is now in progress. Good luck!`,
      variant: "success",
    })
  }

  const handleDeleteWorkout = (id) => {
    deleteWorkout(id)
    toast({
      title: "Workout Deleted",
      description: "The workout has been removed from your plans.",
    })
  }

  // Sample workout templates
  const workoutTemplates = [
    {
      id: 'template-1',
      name: 'Full Body Strength',
      category: 'strength',
      duration: 45,
      exercises: 8,
      difficulty: 'Intermediate',
      description: 'Complete full-body workout targeting all major muscle groups',
      image: '🏋️'
    },
    {
      id: 'template-2',
      name: 'HIIT Cardio Blast',
      category: 'cardio',
      duration: 30,
      exercises: 6,
      difficulty: 'Advanced',
      description: 'High-intensity interval training for maximum calorie burn',
      image: '🔥'
    },
    {
      id: 'template-3',
      name: 'Morning Yoga Flow',
      category: 'flexibility',
      duration: 20,
      exercises: 10,
      difficulty: 'Beginner',
      description: 'Gentle yoga sequence to start your day with energy',
      image: '🧘'
    }
  ]

  return (
    <div className="app-page">
      <div className="app-page-header">
        <div>
          <h1 className="app-page-heading">Workout Planner</h1>
          <p className="app-page-subheading">Plan, track, and execute your fitness routines</p>
        </div>
        <Button 
          variant="fitness" 
          onClick={() => setShowCreateForm(true)}
          className="w-full sm:w-auto"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Workout
        </Button>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search workouts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto">
              {categories.map((category) => {
                const Icon = category.icon
                return (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? "selected" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="whitespace-nowrap"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {category.name}
                  </Button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Workout Templates */}
      <div>
        <h2 className="mb-4 text-xl font-semibold text-slate-900">Popular Templates</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {workoutTemplates.map((template, index) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="h-full">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <div className="text-4xl mb-2">{template.image}</div>
                    <h3 className="text-lg font-semibold text-slate-900">{template.name}</h3>
                    <p className="mt-1 text-sm text-slate-500">{template.description}</p>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Duration</span>
                      <span className="font-medium">{template.duration} min</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Exercises</span>
                      <span className="font-medium">{template.exercises}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Difficulty</span>
                      <span className={`font-medium ${
                        template.difficulty === 'Beginner' ? 'text-green-600' :
                        template.difficulty === 'Intermediate' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {template.difficulty}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="fitness" size="sm" className="flex-1">
                      <Play className="h-4 w-4 mr-2" />
                      Start
                    </Button>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* My Workouts */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900">My Workouts</h2>
          <span className="text-sm text-slate-500">{filteredWorkouts.length} workouts</span>
        </div>
        
        {filteredWorkouts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Dumbbell className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="mb-2 text-lg font-medium text-slate-900">No workouts found</h3>
              <p className="mb-6 text-slate-500">
                {searchTerm || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'Create your first workout to get started'
                }
              </p>
              <Button variant="fitness" onClick={() => setShowCreateForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Workout
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkouts.map((workout, index) => (
              <motion.div
                key={workout.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{workout.name}</CardTitle>
                        <p className="mt-1 text-sm text-slate-500">
                          {workout.category} • {formatDuration(workout.duration)}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <Button variant="ghost" size="sm">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteWorkout(workout.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-sm text-slate-500">
                        <Target className="h-4 w-4 mr-2" />
                        <span>{workout.exercises?.length || 0} exercises</span>
                      </div>
                      <div className="flex items-center text-sm text-slate-500">
                        <Calendar className="h-4 w-4 mr-2" />
                        <span>Created {formatDate(workout.createdAt)}</span>
                      </div>
                      {workout.lastCompleted && (
                        <div className="flex items-center text-sm text-slate-500">
                          <Clock className="h-4 w-4 mr-2" />
                          <span>Last: {formatDate(workout.lastCompleted)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="fitness" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleStartWorkout(workout)}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Start Workout
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <div className="mb-1 text-2xl font-semibold text-slate-900">
              {workouts.filter(w => w.status === 'completed').length}
            </div>
            <div className="text-sm text-slate-500">Completed Workouts</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <div className="mb-1 text-2xl font-semibold text-slate-900">
              {Math.round(workouts.reduce((sum, w) => sum + (w.actualDuration || 0), 0) / 60)}h
            </div>
            <div className="text-sm text-slate-500">Total Time</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <div className="mb-1 text-2xl font-semibold text-slate-900">
              {workouts.filter(w => w.status === 'completed' && 
                new Date(w.completedAt) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
              ).length}
            </div>
            <div className="text-sm text-slate-500">This Week</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <div className="mb-1 text-2xl font-semibold text-slate-900">
              {exercises.length}
            </div>
            <div className="text-sm text-slate-500">Available Exercises</div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default WorkoutPlanner
